import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";

export interface TimelineEvent {
  type:
    | "quote_created"
    | "message_sent"
    | "reply_received"
    | "draft_proposed"
    | "conversation_note"
    | "review_invite_sent"
    | "review_completed"
    | "opt_out"
    | "staff_reply_sent"
    | "voice_followup"
    | "vonigo_status_change";
  ts: string;
  quote_id: string | null;
  label: string;
  payload?: Record<string, unknown>;
}

export interface CustomerSummary {
  id: number;
  full_name: string;
  email: string;
  phone?: string;
  account_label?: string;
  first_seen_at?: string;
  last_seen_at?: string;
}

export interface QuoteSummary {
  quote_id: string;
  status: string;
  raw_label?: string;
  total_usd: number;
  created_on: string;
  created_by?: string;
  marketing_campaign?: string;
  first_seen_at?: string;
  last_seen_at?: string;
}

export interface ComplianceFlags {
  has_opt_out: boolean;
  opt_outs: Array<{ channel: string; reason?: string; opted_out_at: string }>;
}

export interface NextBestAction {
  action: "call_now" | "approve_draft" | "open_vonigo" | "wait" | "inspect_timeline";
  priority: "critical" | "high" | "normal" | "low";
  reason: string;
  quote_id?: string;
  email_response_id?: number;
  amount?: number;
}

export interface ClientGhost {
  ghosted: boolean;
  ghost_reason: string | null;
  ghosted_by: string | null;
  ghosted_at: string | null;
}

export interface Customer360 {
  customer: CustomerSummary;
  ghost?: ClientGhost;
  quotes: QuoteSummary[];
  timeline: TimelineEvent[];
  compliance_flags: ComplianceFlags;
  next_best_action: NextBestAction;
  generated_at: string;
}

export function useSetCustomerGhost(contactId: number | null) {
  const headers = useAuthHeaders(true);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { ghosted: boolean; reason?: string }) => {
      const res = await fetch(`/api/cockpit/customers/${contactId}/ghost`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.reason || body.error || "Ghost update failed");
      return body as { ok: true; ghost: ClientGhost };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-360", contactId] });
      queryClient.invalidateQueries({ queryKey: ["quote-360"] });
    },
  });
}

export function useCustomer360(contactId: number | null) {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["customer-360", contactId],
    queryFn: async () => {
      const res = await fetch(`/api/cockpit/customers/${contactId}/360`, {
        headers,
      });
      if (!res.ok) throw new Error("Customer 360 failed");
      return res.json() as Promise<Customer360>;
    },
    enabled: !!contactId,
    staleTime: 5 * 60_000,
  });
}
